import React from 'react';
import { Box, Typography, TextField, Button, Paper, CircularProgress, Alert } from '@mui/material';
import type { DraftCapability } from '../../../services/workflowConfigService';

interface WorkflowDraftPanelProps {
  loading: boolean;
  error: string | null;
  rawLlmNotes: string | null;
  hasPendingDraft: boolean;
  onGenerate: (description: string) => void;
  onAccept: () => void;
  onDiscard: () => void;
  /** NEW — always present going forward; null only for pre-catalog drafts (ref: 06_...md §5). */
  capability?: DraftCapability | null;
  /** NEW — controlled textarea value. Owned by the parent (rather than local state) so a
   * suggestion chip can set it directly, and so the parent can render its own external Generate
   * button (see hideGenerateButton) that still reads the current text. */
  description: string;
  onDescriptionChange: (value: string) => void;
  /** NEW — lets the parent suppress this panel's own "Describe a business rule" label when it
   * renders its own heading above this component, so the two don't visually stack. */
  hideTitle?: boolean;
  /** NEW — lets the parent render its own Generate button outside/below this panel (e.g.
   * alongside a Back button) instead of the one built into this component, avoiding a duplicate. */
  hideGenerateButton?: boolean;
  /** NEW — suppresses the textarea itself. Used when the parent renders this component a second
   * time purely for its generated-output section (error/gap alert/rationale/accept/discard),
   * placed outside the input field's own bordered container. */
  hideInput?: boolean;
  /** NEW — suppresses the generated-output section (error/gap alert/rationale/accept/discard).
   * Used on the instance still rendering the textarea, so that output isn't shown twice — the
   * parent's second instance (hideInput) renders it instead, outside the input's container. */
  hideGeneratedContent?: boolean;
}

/** "Describe a business rule" panel — generate, review raw_llm_notes, then accept/discard (ref: spec §5.4). */
const WorkflowDraftPanel: React.FC<WorkflowDraftPanelProps> = ({
  loading,
  error,
  rawLlmNotes,
  hasPendingDraft,
  onGenerate,
  onAccept,
  onDiscard,
  capability = null,
  description,
  onDescriptionChange,
  hideTitle = false,
  hideGenerateButton = false,
  hideInput = false,
  hideGeneratedContent = false,
}) => {
  const isBlockingGap = Boolean(capability && capability.tier === 3 && !capability.can_generate);

  return (
    <Box>
      {!hideInput && (
        <>
          {!hideTitle && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Describe a business rule
            </Typography>
          )}
          <TextField
            placeholder="e.g. When a leave request comes in, classify urgency, notify the manager, and wait for approval before confirming."
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            size="small"
            disabled={loading}
            sx={{ mb: hideGenerateButton ? 0 : 3 }}
          />
          {!hideGenerateButton && (
            <Button
              variant="contained"
              size="small"
              disabled={loading || !description.trim()}
              onClick={() => onGenerate(description.trim())}
              sx={{ textTransform: 'none', mt: 1 }}
            >
              {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Generate
            </Button>
          )}
        </>
      )}

      {!hideGeneratedContent && (
        <>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {isBlockingGap && hasPendingDraft && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                This request can't be fully built yet
              </Typography>
              {capability!.missing.map((gap, i) => (
                <Typography key={i} variant="body2" sx={{ mb: i < capability!.missing.length - 1 ? 0.5 : 0 }}>
                  • {gap.message}
                </Typography>
              ))}
            </Alert>
          )}

          {rawLlmNotes && hasPendingDraft && (
            <>
              <Paper sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: 1, backgroundColor: 'background.paper' }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Model rationale — review before accepting
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                  {rawLlmNotes}
                </Typography>
              </Paper>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
                <Button size="small" variant="contained" onClick={onAccept} sx={{ textTransform: 'none' }}>
                  Accept
                </Button>
                <Button size="small" variant="outlined" onClick={onDiscard} sx={{ textTransform: 'none' }}>
                  Discard
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default WorkflowDraftPanel;
